import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { usePageTitle } from "@/hooks/use-page-title";
import { authApi, type OAuthProvider } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { takePostLoginReturnTo } from "@/lib/post-login-return-to";
import { takePendingSignIn } from "@/lib/pkce";
import { useAuthStore } from "@/stores/auth-store";

const PROVIDER_NAME: Record<OAuthProvider, string> = { google: "Google", microsoft: "Microsoft" };

/**
 * The provider redirects here (`/auth/<provider>/callback?code=...&state=...`).
 * The `state` must match the one stored before the redirect (CSRF); the code
 * is then exchanged by the API together with the PKCE verifier.
 */
export function OAuthCallbackPage({ provider }: { provider: OAuthProvider }) {
  const providerName = PROVIDER_NAME[provider];
  usePageTitle("Signing in");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const providerError = params.get("error");
    const code = params.get("code");
    const state = params.get("state");
    const pending = takePendingSignIn(provider);

    if (providerError) {
      setError(
        providerError === "access_denied"
          ? `${providerName} sign-in was cancelled.`
          : `${providerName} sign-in failed.`
      );
      return;
    }
    if (!code || !state || pending?.state !== state) {
      setError("This sign-in link is invalid or has expired. Please try again.");
      return;
    }

    authApi
      .oauthCallback(provider, { code, state, codeVerifier: pending.verifier })
      .then((session) => {
        setSession(session);
        void navigate(takePostLoginReturnTo() ?? "/dashboard", { replace: true });
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : `${providerName} sign-in failed.`);
      });
  }, [params, navigate, setSession, provider, providerName]);

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10">
      <BrandMark />
      <div className="mt-10 w-full max-w-md rounded-lg border border-border bg-card p-8 text-center">
        {error ? (
          <div className="space-y-4">
            <h1 className="text-xl font-semibold text-foreground">Couldn&apos;t sign you in</h1>
            <p className="text-sm text-muted-foreground" role="alert">
              {error}
            </p>
            <Link to="/login" className="block text-sm text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3" role="status">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
            <p className="text-sm text-muted-foreground">Signing you in with {providerName}...</p>
          </div>
        )}
      </div>
    </div>
  );
}
